/* eslint-disable @typescript-eslint/no-explicit-any */
import {
	Input,
	Dropdown,
	Option,
	makeStyles,
	tokens,
	Combobox,
	useComboboxFilter
} from '@fluentui/react-components';
import { Filter16Regular } from '@fluentui/react-icons';
import { Item, useAutomationRuleListController } from './automation-rule-list-provider';
import { useCallback, useState } from 'react';
import { useAzureProvider } from 'src/azure/context';
import { getAutomationRuleListByFilterService } from 'src/services/appgami-api/get-automation-rule-by-filter-service';
import { identitiesList } from './utils/list-identities';

const statusOptions = [
	{ label: 'Enabled', value: 'enabled' },
	{ label: 'Disabled', value: 'disabled' }
];

function TextInputFilter(props: { value: string; onChange(text: string): void }) {
	const classes = useStyles();

	return (
		<div className={classes.root}>
			<Input
				placeholder='Search rules'
				className={classes.input}
				contentBefore={<Filter16Regular />}
				appearance="underline"
				value={props.value}
				onChange={(ev) => {
					props.onChange(ev.target.value);
				}}
			/>
		</div>
	);
}

let debounce: any = null;
let filterByTextDebounce: any = null;

export default function AutomationRuleListFilterContent() {
	const classes = useStyles();

	const { applicationContext: { organizationName, projectId } } = useAzureProvider();
	const {setList, list, setLoadingAutomationRuleList, setSelectedItems, loadAutomationRuleList, authorList, authorListQuery, setAuthorListQuery} = useAutomationRuleListController();

	const [status, setStatus] = useState<string | (readonly string[] & string) | undefined>('');
	const [selectedStatus, setSelectedStatus] = useState<Array<string>>([]);

	const [createdBy, setCreatedBy] = useState<string | (readonly string[] & string) | undefined>('');
	const [selectedCreatedBy, setSelectedCreatedBy] = useState<Array<string>>([]);

	const [partialName, setPartialName] = useState('');

	const filterByName = useCallback(async (text: string) => {
		clearTimeout(filterByTextDebounce);
		setLoadingAutomationRuleList(true);

		if (!text) {
			await loadAutomationRuleList();
		}
	
		if (text) {
			filterByTextDebounce = setTimeout(() => {
				const results = list.filter((item) => {
					return new RegExp(text.toLowerCase()).test(item.name.label.toLowerCase());
				});
	
				const sortedList = results.sort((a, b) => a.automationRuleItem.name.localeCompare(b.automationRuleItem.name, undefined, { sensitivity: 'base' }));
				setList(sortedList);
				setLoadingAutomationRuleList(false);
			}, 1000);
		}
	}, [list]);

	const usersFilterContent = useComboboxFilter(
		authorListQuery,
		authorList.map(item => item.value),
		{
			filter: (optionText, query) => {
				const foundLabel = authorList.find(item => item.value === optionText)?.label || '';
				return new RegExp(query.toLowerCase()).test(foundLabel.toLowerCase());
			},
			renderOption: (optionValue) => {
				const found = authorList.find(item => item.value === optionValue);
				const label = found?.label || '';
				const value = found?.value || '';
				
				return <Option text={label} value={value}>{label}</Option>;
			},
			noOptionsMessage: 'No option match your search.',
		}
	);

	const loadAutomationRuleByFilter = useCallback((values: {
		status: Array<string>;
		createdBy: Array<string>;
	}) => {
		try {
			const asyncLoadListByFilter = async () => {
				const results = await getAutomationRuleListByFilterService({
					organizationName: organizationName,
					projectId: projectId,
					filter: {
						createdBy: values.createdBy.length > 0 ? values.createdBy.join(',') : undefined,
						status: values.status.length > 0 ? values.status.join(',') : undefined,
					}
				});

				if (results) {
					const identities = identitiesList();
					const values: Array<Item> = await Promise.all(
						results
							.map(async (automation) => {
								await identities.search(automation.created_by);
								const identity: any = identities.getIdentity(automation.created_by);
								return {
									automationRuleItem: automation,
									name: { label: automation.name },
									status: { label: automation.status },
									author: {
										name: identity.displayName,
										image: `https://dev.azure.com/${identity.image}`,
										mail: identity.mail
									},
									createdAt: {
										date: automation.created_at
									}
								};
							})
					);
					const sortedList = values.sort((a, b) => a.automationRuleItem.name.localeCompare(b.automationRuleItem.name, undefined, { sensitivity: 'base' }));
					setList(sortedList);
				}

				if (!results) {
					setList([]);
				}

				setSelectedItems([]);
				setLoadingAutomationRuleList(false);
			};

			const asyncLoadList = async () => {
				await loadAutomationRuleList();
			};

			setLoadingAutomationRuleList(true);
			clearTimeout(debounce);

			if ([
				!values.createdBy.length,
				!values.status.length
			].every(itsTrue => itsTrue)) {
				debounce = setTimeout(asyncLoadList, 1000);
			} else {
				debounce = setTimeout(asyncLoadListByFilter, 1000);
			}
		} catch (err) {
			console.error('Find automation rule list by filter error.');
			console.error(err);
		}
	}, [organizationName, projectId]);

	return (
		<div className={classes.filterRoot}>
			<div className={classes.filterInput}>
				<TextInputFilter
					value={partialName}
					onChange={(text) => {
						setPartialName(text);
						filterByName(text);
					}}
				/>
			</div>
			<div className={classes.filterDropdownWrapper}>
				<Dropdown
					className={classes.dropdown}
					appearance="underline"
					placeholder="Status"
					positioning="below-end"
					multiselect
					value={status}
					onOptionSelect={(_, data) => {
						setStatus(() => {
							if (data.selectedOptions.length > 1) {
								return 'Enabled (+1)';
							}

							if (!data.selectedOptions.length) {
								return '';
							}

							return data.optionText;
						});

						setSelectedStatus(data.selectedOptions);
						loadAutomationRuleByFilter({
							status: data.selectedOptions,
							createdBy: selectedCreatedBy
						});
					}}
				>
					{statusOptions.map((option) => (
						<Option key={option.value} value={option.value}>
							{option.label}
						</Option>
					))}
				</Dropdown>
				<Combobox
					className={classes.dropdown}
					appearance="underline"
					placeholder='Author'
					positioning="below-end"
					multiselect
					value={authorListQuery}
					onChange={(ev) => {
						setAuthorListQuery(ev.target.value);
					}}
					onFocus={() => {
						setAuthorListQuery('');
					}}
					onBlur={() => {
						setAuthorListQuery(createdBy as string);
					}}
					onOptionSelect={(_, data) => {
						setCreatedBy((odlValue) => {
							if (data.selectedOptions.length > 1) {
								return `${odlValue} (+${data.selectedOptions.length})`;
							}

							if (!data.selectedOptions.length) {
								return '';
							}

							return data.optionText;
						}); 

						setSelectedCreatedBy(data.selectedOptions);
						loadAutomationRuleByFilter({
							status: selectedStatus,
							createdBy: data.selectedOptions
						});
					}}
				>
					{usersFilterContent}
					{/* {authorList.map((author) => (
						<Option key={author.value} value={author.value}>
							{author.label}
						</Option>
					))} */}
				</Combobox>
			</div>
		</div>
	);
}

const useStyles = makeStyles({
	root: {
		boxSizing: 'border-box',
	},
	input: {
		width: '100%',
		borderBottom: 'none',
		'::after': {
			borderBottom: 'none'
		},
		'> input': {
			paddingLeft: '8px',
		},
		':has(input:focus)': {
			borderRadius: tokens.borderRadiusMedium,
			boxShadow: '0 0 0 3px rgba(0, 120, 212, .23), 0 0 0 1px rgba(0, 120, 212, 1)',
		},
	},
	filterRoot: {
		display: 'flex',
		justifyContent: 'space-between',
		gap: '16px'
	},
	filterInput: {
		width: '100%'
	},
	dropdown: {
		minWidth: '120px',
		borderBottom: 'none',
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		'::after': {
			borderBottom: 'none'
		},
		':hover': {
			background: tokens.colorNeutralShadowAmbientLighter
		},
	},
	filterDropdownWrapper: {
		display: 'flex',
		gap: '16px'
	},
});
